/**
 * Shared marketplace product detail modal.
 *
 * Renders the same rich product detail (Overview, Key Features, Specifications,
 * Applications, Add-to-Cart / Request-Quote actions) that the Marketplace
 * uses, so any landing page (Sterile Processing, Dialysis, Green Klean, etc.)
 * can wire it in with a single import.
 *
 * Contract for the `product` prop matches the shape produced by
 * `buildSterileProcessingProducts()` and the inline marketplace catalog:
 *   { id, name, brand, tagline, description, image, features[], specs{},
 *     applications[], customer_price?, case_price?, unit_price?, pack?,
 *     variants?, category, brochureUrl? }
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { CheckCircle2, ShoppingCart, FileText, Download, Phone } from "lucide-react";

export default function ProductDetailModal({ product, onClose }) {
  const { addItem } = useQuoteCart();
  if (!product) return null;

  const casePrice = Number(product.case_price ?? product.customer_price ?? 0);
  const unitPrice = Number(product.unit_price ?? 0);
  const hasPrice = casePrice > 0 || unitPrice > 0;
  const displayPrice = casePrice > 0 ? casePrice : unitPrice;
  const priceLabel = casePrice > 0 ? "/ case" : "/ unit";

  const handleAddToCart = () => {
    addItem({
      erp: product.specs?.SKU || product.id,
      name: product.name,
      image: product.image,
      source: product.source || "marketplace",
      category: product.category,
      unit_price: displayPrice,
      list_price: displayPrice,
      pack:
        (typeof product.pack === "string" && product.pack) ||
        product.pack?.display ||
        (casePrice > 0 ? "case" : "unit"),
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="product-detail-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-50 to-slate-100">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-72 md:h-80 object-contain p-6"
          />
          {/* Bottom-only scrim so the title stays legible without dimming the product photo */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/85 via-black/50 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            {product.brand && <p className="text-emerald-300 font-medium mb-1 drop-shadow">{product.brand}</p>}
            <h2 className="text-2xl font-bold text-white mb-2 drop-shadow" data-testid="product-detail-name">{product.name}</h2>
            {product.tagline && <p className="text-white/90 drop-shadow">{product.tagline}</p>}
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-slate-900/60 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-slate-900/80 transition-colors"
            aria-label="Close"
            data-testid="product-detail-close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div>
            <h3 className="font-bold text-slate-800 mb-2">Overview</h3>
            <p className="text-slate-600">{product.description}</p>
          </div>

          {product.features?.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-800 mb-3">Key Features</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {product.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div>
              <h3 className="font-bold text-slate-800 mb-3">Specifications</h3>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.entries(product.specs).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-slate-200 pb-2">
                      <span className="text-slate-500 text-sm">{key}</span>
                      <span className="text-slate-800 font-medium text-sm">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sol-M / MicroCare variants (case + unit prices per pack size) */}
          {product.variants?.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-800 mb-3">Available Pack Sizes</h3>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">SKU</th>
                      <th className="text-left px-3 py-2 font-semibold">Pack</th>
                      <th className="text-right px-3 py-2 font-semibold">Case Price</th>
                      <th className="text-right px-3 py-2 font-semibold">Unit Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.variants.map((v, i) => {
                      const packLabel =
                        (typeof v.pack === "string" && v.pack) ||
                        v.pack?.display ||
                        v.description ||
                        "—";
                      return (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-slate-900">{v.sku || "—"}</td>
                          <td className="px-3 py-2 text-slate-700">{packLabel}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            {v.case_price ? `$${Number(v.case_price).toFixed(2)}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {v.unit_price ? `$${Number(v.unit_price).toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {product.applications?.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-800 mb-3">Applications</h3>
              <div className="flex flex-wrap gap-2">
                {product.applications.map((app, i) => (
                  <Badge key={i} variant="outline" className="bg-slate-50">
                    {app}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
            {hasPrice ? (
              <>
                <div className="flex items-baseline gap-2 mr-auto">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Price</span>
                  <span className="text-2xl font-black text-emerald-700" data-testid="product-detail-price">
                    ${Number(displayPrice).toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-500">{priceLabel}</span>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="modal-add-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Quote Cart
                </Button>
              </>
            ) : (
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                <Link
                  to={`/hospital/backorders?prefill=1&source=marketplace&item_name=${encodeURIComponent(product.name)}&mfr_number=${encodeURIComponent(product.specs?.SKU || product.id || "")}&brand=${encodeURIComponent(product.brand || "")}`}
                  data-testid="modal-request-quote-btn"
                  onClick={onClose}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Request Quote
                </Link>
              </Button>
            )}
            {product.brochureUrl && (
              <Button variant="outline" asChild>
                <a href={product.brochureUrl} target="_blank" rel="noopener noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  Download Brochure
                </a>
              </Button>
            )}
            <Button variant="outline" asChild>
              <a href="tel:8009883892">
                <Phone className="w-4 h-4 mr-2" />
                800.988.3892
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
